import { create } from "zustand";
import { devtools } from "zustand/middleware";
import _get from "lodash-es/get";

import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import { FLOW_UPDATE_TYPE, buildIdentifierBody, PAYMENT_STATUS, SOR_STATUS } from "utils/constants/onboarding";

const errMessage = (error, fallback) =>
  _get(error, ["response", "data", "error", "message"], fallback);

const cleanText = (value) => (value ?? "").toString().trim();

const fetchRazorpayBankDetails = async (ifscCode) => {
  const ifsc = cleanText(ifscCode).toUpperCase();
  if (!ifsc) return {};
  const res = await fetch(`https://ifsc.razorpay.com/${encodeURIComponent(ifsc)}`);
  if (!res.ok) throw new Error("Unable to fetch IFSC details");
  const data = await res.json();
  return {
    bankName: cleanText(data?.BANK),
    bankAddress: cleanText(data?.ADDRESS),
  };
};

const sentenceCase = (value = "") => {
  const text = value.toString().trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const collectSubstepLabels = (flow) => {
  const labels = {};
  const visit = (substep) => {
    if (!substep?.substep_id) return;
    labels[substep.substep_id] = substep.title || substep.subtitle || "this field";
    (substep.input_todo_list?.items ?? []).forEach((item) => {
      labels[item.substep_id] = item.title || labels[substep.substep_id];
      (item.primitive_inputs ?? []).forEach(visit);
    });
  };

  (flow?.flow_config?.steps ?? []).forEach((step) =>
    (step?.substeps ?? []).forEach(visit)
  );
  return labels;
};

const friendlySubstepError = (message = "", label = "this field") => {
  let text = cleanText(message);
  if (!text) return "";

  const userMessageParts = text.split(/UserMessage\s*:\s*/i);
  if (userMessageParts.length > 1) text = cleanText(userMessageParts[userMessageParts.length - 1]);

  text = text
    .replace(/Orig\w*Error\s*:\s*/gi, "")
    .replace(/ErrorType\s*:\s*[A-Z_]+,?/gi, "")
    .trim();

  const prefixed = text.match(/^([a-z0-9_]+)\s*:\s*(.+)$/i);
  if (prefixed) text = cleanText(prefixed[2]);

  text = text.replace(/,\s*status:.*$/i, "").trim();

  if (/_/.test(text) || /\bsubstep[_\s-]*id\b/i.test(text)) {
    if (/required|mandatory|missing|empty/i.test(text)) {
      return `Please enter ${label}.`;
    }
    return `Please check ${label} and try again.`;
  }

  return sentenceCase(text);
};

const buildSubstepErrorMap = (stepErrors = [], flow) => {
  const labels = collectSubstepLabels(flow);
  const map = {};
  stepErrors.forEach((se) =>
    (se?.substep_errors ?? []).forEach((sse) => {
      if (sse?.substep_id != null && sse.error_message) {
        const message = friendlySubstepError(sse.error_message, labels[sse.substep_id]);
        if (message) map[sse.substep_id] = message;
      }
    })
  );
  return map;
};

const bearerize = (token) =>
  /^bearer\s+/i.test(token) ? token : `Bearer ${token}`;

const writeSession = ({ token }) => {
  if (token) window.localStorage.setItem(LS_KEYS.TOKEN, bearerize(token));
};

const clearSession = () => {
  window.localStorage.removeItem(LS_KEYS.TOKEN);
  window.localStorage.removeItem(LS_KEYS.PHONE_NUMBER_SIGNIN);
  window.localStorage.removeItem(LS_KEYS.EMAIL);
};

export const readToken = () => window.localStorage.getItem(LS_KEYS.TOKEN) || "";

export const readIdentifier = () =>
  useOnboardingStore.getState().identifier || { type: "", value: "" };

// Module-level flags prevent duplicate poll timers across re-renders.
let _paymentPollRunning = false;
let _sorPollRunning = false;

const useOnboardingStore = create(
  devtools((set, get) => ({
    flowId: 0,
    signupOtpStatus: "",
    onboardStatus: "",
    fetchFlowStatus: "",
    saveStatus: "",
    flow: null,
    onboardingFee: 0,
    onboardingPaymentGuid: 0,
    sorStatus: "",
    paymentStatus: "",
    rejectionReason: "",
    sellerEmail: "",
    sellerPhone: "",
    externalVendorId: "",
    substepErrors: {},
    error: null,
    identifier: { type: "", value: "" },
    // Tracks the onboardSelfOnboardingSeller call lifecycle ("" | STATUS_BEGIN | STATUS_DONE | STATUS_FAIL).
    selfOnboardStatus: "",
    // True after Poll A (payment) exhausts all attempts without paymentStatus changing.
    pollPaymentExhausted: false,

    setError: (error) => set({ error }),
    clearError: () => set({ error: null }),
    setFlowId: (flowId) => set({ flowId }),
    clearOnboardingPaymentGuid: () => set({ onboardingPaymentGuid: 0 }),
    clearSubstepErrors: () => set({ substepErrors: {} }),

    reset: () => {
      clearSession();
      _paymentPollRunning = false;
      _sorPollRunning = false;
      set({
        flowId: 0,
        signupOtpStatus: "",
        onboardStatus: "",
        fetchFlowStatus: "",
        saveStatus: "",
        flow: null,
        onboardingFee: 0,
        onboardingPaymentGuid: 0,
        sorStatus: "",
        paymentStatus: "",
        rejectionReason: "",
        sellerEmail: "",
        sellerPhone: "",
        externalVendorId: "",
        substepErrors: {},
        error: null,
        identifier: { type: "", value: "" },
        selfOnboardStatus: "",
        pollPaymentExhausted: false,
      });
    },

    requestOtp: async (identifierType, identifierValue) => {
      try {
        await api.post(API_HOST_WMS, APIendpoints.getSignupOtp, buildIdentifierBody(identifierType, identifierValue));
        return { ok: true };
      } catch (error) {
        return { ok: false, message: errMessage(error, "Failed to send OTP. Try again.") };
      }
    },

    verifySecondChannel: async ({ substepId, identifierValue, otp }) => {
      const { flowId, flow } = get();
      if (!flowId) return { ok: false, message: "Session expired." };
      try {
        const res = await api.post(API_HOST_WMS, APIendpoints.upsertSellerOnboarding, {
          flow_id: flowId,
          type: FLOW_UPDATE_TYPE.SAVE_DRAFT,
          otp,
          save_draft: { values: [{ substep_id: Number(substepId), string_value: identifierValue }] },
        });
        const stepErrors = _get(res, ["data", "response", "result", "save_draft", "step_errors"], []);
        const errMap = buildSubstepErrorMap(stepErrors, flow);
        if (errMap[substepId]) return { ok: false, message: errMap[substepId] };
        return { ok: true };
      } catch (error) {
        return { ok: false, message: errMessage(error, "Could not verify the code.") };
      }
    },

    requestSignupOtp: async (identifierType, identifierValue, resendOtpRequest = false) => {
      set({ signupOtpStatus: STATUS_BEGIN, error: null });
      try {
        await api.post(API_HOST_WMS,APIendpoints.getSignupOtp,buildIdentifierBody(identifierType,identifierValue,resendOtpRequest ? { resend_otp_request: true } : {}));
        set({ signupOtpStatus: STATUS_DONE });
        return { ok: true };
      } catch (error) {
        const message = errMessage(error, "Failed to send OTP. Try again.");
        set({ signupOtpStatus: STATUS_FAIL, error: { message } });
        return { ok: false, message };
      }
    },

    verifyOtpAndOnboard: async (identifierType, identifierValue, otp) => {
      set({ onboardStatus: STATUS_BEGIN, error: null });
      try {
        const res = await api.post(
          API_HOST_WMS,
          APIendpoints.onboardSeller,
          buildIdentifierBody(identifierType, identifierValue, { otp })
        );
        const headerToken = _get(res, ["headers", "authorization"]) || "";
        const bodyToken = _get(res, ["data", "response", "jwt_token"]) || "";
        const token = headerToken || bodyToken;
        const flowId = _get(res, ["data", "response", "flow_id"]);

        if (!token || !flowId) {
          const message = "Unexpected response from server. Please try again.";
          set({ onboardStatus: STATUS_FAIL, error: { message } });
          return { ok: false, message };
        }

        writeSession({ token });
        set({
          onboardStatus: STATUS_DONE,
          flowId,
          identifier: { type: identifierType, value: (identifierValue || "").trim() },
        });
        return { ok: true };
      } catch (error) {
        const message = errMessage(error, "Wrong OTP entered. Please try again.");
        set({ onboardStatus: STATUS_FAIL, error: { message } });
        return { ok: false, message };
      }
    },

    fetchFlow: async () => {
      if (!readToken()) return { ok: false, message: "Your session has expired. Please start again." };
      set({ fetchFlowStatus: STATUS_BEGIN, error: null });
      try {
        const res = await api.get(API_HOST_WMS, APIendpoints.getSellerOnboarding);
        const flow = _get(res, ["data", "response", "flow_details", "details"], null);
        const onboardingFee = _get(res, ["data", "response", "onboarding_fee"], 0);
        const fetchedPaymentGuid = _get(res, ["data", "response", "payment_guid"], 0);
        const sorStatus = _get(res, ["data", "response", "status"], "");
        const paymentStatus = _get(res, ["data", "response", "payment_status"], "");
        const rejectionReason = _get(res, ["data", "response", "rejection_reason"], "");
        const externalVendorId = _get(res, ["data", "response", "external_vendor_id"], "");

        const serverEmail = _get(res, ["data", "response", "email"], "").trim();
        const serverPhone = _get(res, ["data", "response", "phone_number"], "").trim();
        const { identifier } = get();
        const idPhone = identifier?.type === "phone" ? (identifier.value || "").trim() : "";
        const idEmail = identifier?.type === "email" ? (identifier.value || "").trim() : "";
        const resolvedEmail = serverEmail || idEmail;
        const resolvedPhone = serverPhone || idPhone;

        if (flow && (resolvedEmail || resolvedPhone)) {
          for (const step of flow.flow_config?.steps ?? []) {
            for (const sub of step.substeps ?? []) {
              if (sub.substep_type !== "TEXT_INPUT") continue;
              const existing = (sub.input_text?.value ?? "").trim();
              if (sub.key === "contact_email" && resolvedEmail && !existing) {
                sub.input_text = { ...(sub.input_text ?? {}), value: resolvedEmail };
              }
              if (sub.key === "contact_phone" && resolvedPhone && !existing) {
                sub.input_text = { ...(sub.input_text ?? {}), value: resolvedPhone };
              }
            }
          }
        }

        const newFlowId = flow?.flow_id || get().flowId;
        const stateUpdate = { flow, flowId: newFlowId, fetchFlowStatus: STATUS_DONE, onboardingFee, sorStatus, paymentStatus, rejectionReason, sellerEmail: resolvedEmail, sellerPhone: resolvedPhone, externalVendorId };
        if (fetchedPaymentGuid > 0) stateUpdate.onboardingPaymentGuid = fetchedPaymentGuid;
        set(stateUpdate);

        return { ok: true, flow };
      } catch (error) {
        const message = errMessage(error, "Failed to load your application.");
        set({ fetchFlowStatus: STATUS_FAIL, error: { message } });
        return { ok: false, message };
      }
    },

    saveDraft: async (values) => {
      const { flowId, flow } = get();
      if (!flowId) return { ok: false, message: "Session expired." };
      set({ saveStatus: STATUS_BEGIN, error: null, substepErrors: {} });
      try {
        const res = await api.post(API_HOST_WMS, APIendpoints.upsertSellerOnboarding, {
          flow_id: flowId,
          type: FLOW_UPDATE_TYPE.SAVE_DRAFT,
          save_draft: { values },
        });
        const stepErrors = _get(res, ["data", "response", "result", "save_draft", "step_errors"], []);
        const substepErrors = buildSubstepErrorMap(stepErrors, flow);
        set({ saveStatus: STATUS_DONE, substepErrors });
        return { ok: Object.keys(substepErrors).length === 0, substepErrors };
      } catch (error) {
        const message = errMessage(error, "Failed to save. Please try again.");
        set({ saveStatus: STATUS_FAIL, error: { message } });
        return { ok: false, message };
      }
    },

    saveDraftSubstep: async (substepId, value) => {
      const { flowId, flow } = get();
      if (!flowId) return { ok: false, message: "Session expired." };
      try {
        const clean = { ...value };
        delete clean._previewUrls;
        delete clean.verified;
        const payload = { substep_id: Number(substepId), ...clean };
        const res = await api.post(API_HOST_WMS, APIendpoints.upsertSellerOnboarding, {
          flow_id: flowId,
          type: FLOW_UPDATE_TYPE.SAVE_DRAFT,
          save_draft: { values: [payload] },
        });
        const stepErrors = _get(res, ["data", "response", "result", "save_draft", "step_errors"], []);
        const allMap = buildSubstepErrorMap(stepErrors, flow);
        const thisErr = allMap[substepId];
        set((s) => {
          const next = { ...s.substepErrors };
          if (thisErr) next[substepId] = thisErr;
          else delete next[substepId];
          return { substepErrors: next, saveStatus: STATUS_DONE };
        });
        return { ok: !thisErr, error: thisErr };
      } catch (error) {
        const message = errMessage(error, "Could not save.");
        set({ error: { message } });
        return { ok: false, message };
      }
    },

    markStepComplete: async (stepId, values) => {
      const { flowId, flow } = get();
      if (!flowId) return { ok: false, message: "Session expired." };
      set({ saveStatus: STATUS_BEGIN, error: null, substepErrors: {} });
      try {
        const res = await api.post(API_HOST_WMS, APIendpoints.upsertSellerOnboarding, {
          flow_id: flowId,
          type: FLOW_UPDATE_TYPE.MARK_STEP_COMPLETE,
          mark_step_complete: { step_id: stepId, values },
        });
        const stepErrors = _get(res, ["data", "response", "result", "mark_step_complete", "step_errors"], []);
        const substepErrors = buildSubstepErrorMap(stepErrors, flow);
        if (Object.keys(substepErrors).length > 0) {
          set({ saveStatus: STATUS_DONE, substepErrors });
          return { ok: false, substepErrors };
        }
        await get().fetchFlow();
        set({ saveStatus: STATUS_DONE });
        return { ok: true };
      } catch (error) {
        const message = errMessage(error, "Could not complete this step.");
        set({ saveStatus: STATUS_FAIL, error: { message } });
        return { ok: false, message };
      }
    },

    // Backend uses r.PostFormValue — send form-encoded, not JSON.
    verifyBankDetails: async (accountNumber, confirmAccountNumber, ifscCode, options = {}) => {
      try {
        const formData = new URLSearchParams();
        formData.set("bank_account_number", accountNumber);
        formData.set("re_bank_account_number", confirmAccountNumber);
        formData.set("ifsc_code", ifscCode);
        const res = await api.post(API_HOST_WMS, APIendpoints.verifyBankDetails, formData);
        const bankData = _get(res, ["data", "response", "bank_account_data"], {});
        const accountHolderName = cleanText(bankData?.account_holder_name);
        let bankName = cleanText(bankData?.bank_name);
        let bankAddress = cleanText(bankData?.bank_address);

        const needsBankName = !!options.requireBankName;
        const needsBankAddress = !!options.requireBankAddress;
        if ((needsBankName && !bankName) || (needsBankAddress && !bankAddress)) {
          const razorpayDetails = await fetchRazorpayBankDetails(ifscCode);
          bankName = bankName || razorpayDetails.bankName;
          bankAddress = bankAddress || razorpayDetails.bankAddress;
        }

        if (!accountHolderName || (needsBankName && !bankName) || (needsBankAddress && !bankAddress)) {
          return {
            ok: false,
            message: "We verified the account, but couldn't fetch complete bank details. Please check the IFSC code and try again.",
          };
        }

        return { ok: true, accountHolderName, bankName, bankAddress };
      } catch (error) {
        return { ok: false, message: errMessage(error, "Bank verification failed. Check account number and IFSC.") };
      }
    },

    onboardSelfOnboardingSeller: async () => {
      set({ selfOnboardStatus: STATUS_BEGIN });
      try {
        const res = await api.post(API_HOST_WMS, APIendpoints.onboardSelfOnboardingSeller, {});
        const status = _get(res, ["data", "response", "status"], "");
        const message = _get(res, ["data", "response", "message"], "");
        const paymentGuid = _get(res, ["data", "response", "payment_guid"], 0);
        const nextState = { selfOnboardStatus: STATUS_DONE };
        if (status) nextState.sorStatus = status;
        if (status === SOR_STATUS.IN_PROGRESS) nextState.rejectionReason = "";
        if (paymentGuid > 0) nextState.onboardingPaymentGuid = paymentGuid;
        set(nextState);
        return { ok: true, status, message, paymentGuid };
      } catch (error) {
        const message = errMessage(error, "Failed to activate seller account.");
        // "already initiated" means the call succeeded in a previous attempt — treat as ok.
        const alreadyDone = /already|initiated/i.test(message ?? "");
        set({ selfOnboardStatus: alreadyDone ? STATUS_DONE : STATUS_FAIL });
        return { ok: alreadyDone, message };
      }
    },

    // Poll A — payment status: 5 s × 6 attempts = 30 s max.
    // Runs silently; sets pollPaymentExhausted when all attempts are spent.
    startPaymentPoll: () => {
      if (_paymentPollRunning) return;
      _paymentPollRunning = true;
      set({ pollPaymentExhausted: false });
      let attempts = 0;
      const tick = async () => {
        const { paymentStatus, fetchFlow } = get();
        if (paymentStatus !== PAYMENT_STATUS.INITIATED) {
          _paymentPollRunning = false;
          return;
        }
        attempts++;
        if (attempts > 6) {
          _paymentPollRunning = false;
          set({ pollPaymentExhausted: true });
          return;
        }
        await fetchFlow();
        // Re-read after fetchFlow in case it changed.
        if (get().paymentStatus === PAYMENT_STATUS.INITIATED) {
          setTimeout(tick, 5000);
        } else {
          _paymentPollRunning = false;
        }
      };
      setTimeout(tick, 5000);
    },

    // Refresh SOR status once after 10 seconds.
    startSorPoll: () => {
      if (_sorPollRunning) return;
      _sorPollRunning = true;
      setTimeout(async () => {
        const { sorStatus, fetchFlow } = get();
        if (sorStatus === SOR_STATUS.INITIATED) await fetchFlow();
        _sorPollRunning = false;
      }, 10000);
    },

    completeFlow: async () => {
      const { flowId, flow } = get();
      if (!flowId) return { ok: false, message: "Session expired." };
      set({ saveStatus: STATUS_BEGIN, error: null, substepErrors: {} });
      try {
        const res = await api.post(API_HOST_WMS, APIendpoints.upsertSellerOnboarding, {
          flow_id: flowId,
          type: FLOW_UPDATE_TYPE.COMPLETE_FLOW,
        });
        const stepErrors = _get(res, ["data", "response", "result", "complete_flow", "step_errors"], []);
        const substepErrors = buildSubstepErrorMap(stepErrors, flow);
        if (Object.keys(substepErrors).length > 0) {
          set({ saveStatus: STATUS_DONE, substepErrors });
          return { ok: false, substepErrors };
        }
        await get().fetchFlow();
        set({ saveStatus: STATUS_DONE });
        return { ok: true };
      } catch (error) {
        const message = errMessage(error, "Could not submit your application.");
        set({ saveStatus: STATUS_FAIL, error: { message } });
        return { ok: false, message };
      }
    },
  }))
);

export default useOnboardingStore;
